import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Fingerprint, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// level: "none" | "view" | "edit"
export default function AccessManagerDialog({ open, onClose, item, onSaved }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [levels, setLevels] = useState({});
  const [requirePasskey, setRequirePasskey] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setRequirePasskey(!!item.require_passkey);
    api.get("/users").then((r) => {
      setUsers(r.data);
      const lv = {};
      r.data.forEach((u) => {
        if ((item.edit_user_ids || []).includes(u.id)) lv[u.id] = "edit";
        else if ((item.view_user_ids || []).includes(u.id)) lv[u.id] = "view";
        else lv[u.id] = "none";
      });
      setLevels(lv);
    });
  }, [open, item]);

  const ownerId = item?.created_by_id;

  const save = async () => {
    setBusy(true);
    try {
      const view_user_ids = [];
      const edit_user_ids = [];
      Object.entries(levels).forEach(([uid, lv]) => {
        if (lv === "view") view_user_ids.push(uid);
        if (lv === "edit") { edit_user_ids.push(uid); view_user_ids.push(uid); }
      });
      await api.put(`/items/${item.id}/permissions`, {
        view_user_ids, edit_user_ids, require_passkey: requirePasskey,
      });
      toast.success("Access updated");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update access");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="access-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#1B4332]" /> Manage access
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <Fingerprint className="w-4 h-4" />
            <div>
              <div className="font-medium">Require passkey to open</div>
              <div className="text-[11px] text-amber-700">Anyone (incl. admins) must re-verify a passkey each time.</div>
            </div>
          </div>
          <Switch checked={requirePasskey} onCheckedChange={setRequirePasskey} data-testid="require-passkey-switch" />
        </div>

        <div className="max-h-[340px] overflow-y-auto vault-scroll -mx-1 px-1 mt-1 divide-y divide-gray-50">
          {users.map((u) => {
            const isOwner = u.id === ownerId;
            const isAdmin = u.role === "admin";
            const locked = isOwner || isAdmin;
            return (
              <div key={u.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`access-row-${u.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {u.name} {u.id === user.id && <span className="text-gray-400">(you)</span>}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{u.email}</div>
                </div>
                {locked ? (
                  <span className="text-xs text-gray-400 shrink-0">{isOwner ? "Owner · full" : "Admin · full"}</span>
                ) : (
                  <Select value={levels[u.id] || "none"} onValueChange={(v) => setLevels((p) => ({ ...p, [u.id]: v }))}>
                    <SelectTrigger className="h-8 w-[140px]" data-testid={`access-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No access</SelectItem>
                      <SelectItem value="view">View only</SelectItem>
                      <SelectItem value="edit">View + Modify</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={busy} className="bg-[#0A0A0A] hover:bg-gray-800" data-testid="save-access-btn">
            {busy ? "Saving…" : "Save access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
